import React, { useEffect, Suspense, lazy } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { clearAllOnboardingV2Drafts } from './components/onboarding-v2/draft'
import { qaBypassEnabled } from './lib/qaBypass'
import * as Sentry from '@sentry/react'
import './index.css'
// Design System v2 · tokens raíz (P2 · 2026-05-28)
import './design-system/tokens/tokens.css'
import { supabase } from './lib/supabase'
import useAuthStore from './store/authStore'
import { registerServiceWorker } from './lib/pushNotifications'
import { ToastProvider } from './context/ToastContext'
import { AuriaContextProvider } from './components/ai/AuriaContextProvider'
import AuriaSheet from './components/ai/AuriaSheet'
// Sprint 19 — PWA utilities
import AddToHomeScreenPrompt from './components/pwa/AddToHomeScreenPrompt'
import SwUpdateBanner from './components/pwa/SwUpdateBanner'

// Eager — auth + onboarding + landing need to render quickly.
// PrimeLab v1.0.0 Acción 5: AuthPage swap legacy aurIA → AuthPagePL identidad PrimeLab.
import AuthPage from './components/auth/AuthPagePL'
import AuthExpiredPage from './components/auth/AuthExpiredPL'
// Parte 3 (BUG ENTRADA) · el splash con logo (PLLoader) ya no se usa entre
// pasos. RouteTransitionFallback es el patrón de carga quieto del DS (cream +
// bolita, sin wordmark) para transiciones intermedias y resolución de auth.
import RouteTransitionFallback from './components/brand/RouteTransitionFallback'
// SALTO:onboarding-10 · T2 · la puerta del alta abierta, en su propio fichero para que el guard monte el mismo componente que monta la ruta.
import AltaAbierta from './components/auth/AltaAbierta'
import AppLayout from './components/layout/AppLayout'
// Cinceladura cables-v3 2026-05-28 · ErrorBoundary local para rutas pesadas ·
// PWA jamás white-screenea · fallback cream + terracota + retry.
import PLErrorBoundary from './components/error/PLErrorBoundary'
// COLA:la-pestana-de-ayer-se-rompe-tras-la-promocion · TODOS los trozos diferidos pasan por aquí: comprueba la versión servida antes de pedir el
// fichero y recarga una vez si el servidor cambió de versión (la pestaña de ayer no puede morir pidiendo un hash que ya no existe).
import { cargaDeTrozo, esErrorDeTrozo, recargaUnaVez } from './lib/cargaDeTrozo'
import CookieConsentBanner from './components/legal/CookieConsentBanner'
// PrimeLab Fase 11.1 — home pública rebrand: legacy LandingPage (aurIA dark+neón)
// reemplazado por LandingPagePL (cream + terracota + ink + brass).
import LandingPage from './components/landing/LandingPagePL'

// Sprint 19 — WearablesHub (legacy · queda como fallback hasta C.1 estabilizado)
const WearablesHubPL = lazy(cargaDeTrozo(() => import('./components/hub/WearablesHubPL')))
// Bloque C.1 (Operación Feten · Fase 2) — Wearables Hub refundido coach absoluto
const WearablesHubPage = lazy(cargaDeTrozo(() => import('./components/wearables-hub/WearablesHubPage')))
// P6 · Hub V2 · día-1 + datos + 3 niveles zoom + sheets
const HubV2Page = lazy(cargaDeTrozo(() => import('./components/hub-v2/HubV2Page')))
// Bloque C.1 · playground público para QA visual sin auth · screenshot audit
const HubPlaygroundC1 = lazy(cargaDeTrozo(() => import('./components/wearables-hub/HubPlaygroundC1')))
// Fase 11.1b · preview público del HUB visual con datos mock (sin auth)
const HubPreviewPage = lazy(cargaDeTrozo(() => import('./components/hub-v2/HubPreviewPage')))
// Los 10 datos canónicos al 11 · cata pública (claro/oscuro · sin auth)
const HubCanonicalShowcase = lazy(cargaDeTrozo(() => import('./components/hub-v2/HubCanonicalShowcase')))
const GoalPhaseMapPreview = lazy(cargaDeTrozo(() => import("./components/today-v2/GoalPhaseMapPreview")))
// TODAY Capa 1 · preview público con los 4 escenarios (sin auth)
const TodayPreviewPage = lazy(cargaDeTrozo(() => import('./components/today-v2/TodayPreviewPage')))

const LifePathShowcase = lazy(cargaDeTrozo(() => import('./components/dev/LifePathShowcase')))
// F4 render-in-loop · showcase de componentes del sistema (sin auth)
const ComponentsShowcase = lazy(cargaDeTrozo(() => import('./components/dev/ComponentsShowcase')))
const PrimeScorePreview = lazy(cargaDeTrozo(() => import('./components/dev/PrimeScorePreview')))
const ReasoningPreview = lazy(cargaDeTrozo(() => import('./components/dev/ReasoningPreview')))
// F4 bug#9 · acordeón de deportes (Block6a real con deportes preseleccionados)
const SportAccordionDemo = lazy(cargaDeTrozo(() => import('./components/dev/SportAccordionDemo')))
// F4 bug#6 · cards de Progress (Resumen + 7 partes) con Card único del DS
const ProgressCardsDemo = lazy(cargaDeTrozo(() => import('./components/dev/ProgressCardsDemo')))
// F4 #7/#8 + F3 · calendario real con planes mock (nutricionista tejido)
const CalendarDemo = lazy(cargaDeTrozo(() => import('./components/dev/CalendarDemo')))
// F2 · Línea de Vida hibridada (barra simple 3 arcos + concierto)
const LifeBarDemo = lazy(cargaDeTrozo(() => import('./components/dev/LifeBarDemo')))
// MACROPROMPT Pieza 1 · la HEROÍNA: card terracota Línea de Vida (3 arcos) · gate visual
const TodayCardShowcase = lazy(cargaDeTrozo(() => import('./components/dev/TodayCardShowcase')))
const FuelingSesionShowcase = lazy(cargaDeTrozo(() => import('./components/dev/FuelingSesionShowcase')))
const PorQueEstosNumerosShowcase = lazy(cargaDeTrozo(() => import('./components/dev/PorQueEstosNumerosShowcase')))
const TodayRespiraShowcase = lazy(cargaDeTrozo(() => import('./components/dev/TodayRespiraShowcase'))) // T5·F1 · cata Today que respira
// BLOQUE 2·2.3 · cata del onboarding sin auth (dev-only)
const OnboardingPreviewShowcase = lazy(cargaDeTrozo(() => import('./components/dev/OnboardingPreviewShowcase')))
const QAPreviewPage = lazy(cargaDeTrozo(() => import('./components/dev/QAPreviewPage')))
const _QA_BYPASS = qaBypassEnabled()
// RUTA DEV EFÍMERA (loop de validación viva · autorizada founder): surfacea el access_token de la
// sesión logueada para que CC lo lea por píxeles. FLAG ON solo mientras se necesita → OFF (false) al acabar.
const SessionTokenDev = lazy(cargaDeTrozo(() => import('./components/dev/SessionTokenDev')))
const _DEV_TOKEN_ROUTE = false // OFF. Se encendió para verificar en fila regenerate-week (item 2) y la
// adaptación por RPE con el techo de R1 (item 1); ambas lecturas están hechas y capturadas, así que
// la ruta vuelve a estar apagada. Nace apagada siempre: se enciende para una lectura y se apaga en el
// mismo turno.
// Bloque C.3 · Calendar + FAB chaos absorption refundido
// P5 · Calendar V2 · 3 vistas DS + dos pilares + chaos labels + P7 stubs
const CalendarV2Page = lazy(cargaDeTrozo(() => import('./components/calendar-v2/CalendarV2Page')))
// Bloque C.4 · Dominios refundido 8 dominios + jerarquía dinámica
const DomainsC4Page = lazy(cargaDeTrozo(() => import('./components/domains-c4/DomainsC4Page')))
// P5 · Dominios V2 · 8 cards anatomía nueva + cross-domain chips + nota global
const DominiosV2Page = lazy(cargaDeTrozo(() => import('./components/dominios-v2/DominiosV2Page')))
// Bloque C.5 · Progreso refundido 4 niveles
const ProgressC5Page = lazy(cargaDeTrozo(() => import('./components/progress-c5/ProgressC5Page')))
// P6 · Progress V2 · hero trayectoria + tendencias + radar + understanding reframe
const ProgressV2Page = lazy(cargaDeTrozo(() => import('./components/progress-v2/ProgressV2Page')))
// Bloque C.2 · Integrations page Strava + wearables
const IntegrationsPage = lazy(cargaDeTrozo(() => import('./components/integrations/IntegrationsPage')))
// Bloque C.8 · Onboarding sinfónico 6 bloques (V1 · back-compat)
const OnboardingSinfonicoPage = lazy(cargaDeTrozo(() => import('./components/onboarding-c8/OnboardingSinfonicoPage')))
// P3 · Onboarding V2 · doc maestro 1.x · 6 bloques + Resumen Extendido (3 pasos)
const OnboardingV2Page = lazy(cargaDeTrozo(() => import('./components/onboarding-v2/OnboardingV2Page')))
// Cinceladura OPCIÓN A 2026-05-28 · pantalla transición "El Ascenso"
// (4 fases · pipeline real durante animación bolita siguiendo curva canónica).
const AscensoScreenPL = lazy(cargaDeTrozo(() => import('./components/transicion/AscensoScreenPL')))
// SALTO:onboarding-10 · T5 · P1 · la Ficha del 10 resumida al cerrar el alta: existía entera por API y ninguna pantalla la pintaba
const FichaDelAltaScreen = lazy(cargaDeTrozo(() => import('./components/transicion/FichaDelAltaScreen')))
// SALTO:ficha-del-10 · D6 y D8 · las dos pantallas de la Ficha que vivían sólo en la API (#208 y #300).
const PrimeReviewScreen = lazy(cargaDeTrozo(() => import('./components/ficha/PrimeReviewScreen')))
const LoQueNoEntraScreen = lazy(cargaDeTrozo(() => import('./components/ficha/LoQueNoEntraScreen')))
// Sprint 15 Ola 3 — canonical today surface (replaces surfaces/TodayPL)
const TodayPL    = lazy(cargaDeTrozo(() => import('./components/today/TodayPagePL')))
// P4 (2026-05-28) · Today V2 · 3 tercios + bolita-testigo · doc maestro sec 2
const TodayV2Page = lazy(cargaDeTrozo(() => import('./components/today-v2/TodayV2Page')))
// Sprint 15 Ola 4 — new Calendar feature (DayView + chronotimeline)
// Sprint 15 Ola 5 — Domains pedagogical surface
const DomainsPagePL = lazy(cargaDeTrozo(() => import('./components/domains/DomainsPagePL')))
const DomainDeepDive = lazy(cargaDeTrozo(() => import('./components/domains/DomainDeepDive')))
const AriaPL     = lazy(cargaDeTrozo(() => import('./components/surfaces/AriaPL')))
const SessionPL  = lazy(cargaDeTrozo(() => import('./components/surfaces/SessionPL')))
const ProgressPL = lazy(cargaDeTrozo(() => import('./components/surfaces/ProgressPL')))
// Sprint 15 Ola 6 Bloque 1 — new Progress orchestrator
const ProgressPagePL = lazy(cargaDeTrozo(() => import('./components/progress/ProgressPagePL')))
const AICoachPage = lazy(cargaDeTrozo(() => import('./components/ai/AICoachPage')))
const HealthSyncPage = lazy(cargaDeTrozo(() => import('./components/health/HealthSyncPage')))
const WearablesSettings = lazy(cargaDeTrozo(() => import('./components/health/WearablesSettings')))
const MobilityPage = lazy(cargaDeTrozo(() => import('./components/mobility/MobilityPage')))
const PrivacyPage = lazy(cargaDeTrozo(() => import('./components/legal/PrivacyPage')))
const TermsPage = lazy(cargaDeTrozo(() => import('./components/legal/TermsPage')))
// Sprint PrimeLab Fase 1: playground del design system. Ruta pública /playground.
const PLPlaygroundPage = lazy(cargaDeTrozo(() => import('./components/playground/PLPlaygroundPage')))
// P2 · 2026-05-28 · Design System V2 showcase (ruta pública /design-system).
// Validación visual para founder antes de aplicar componentes en pantallas P3-P7.
const DesignSystemPage = lazy(cargaDeTrozo(() => import('./components/design-system-playground/DesignSystemPage')))
// Sprint 14b Phase 3: calibration ritual screen. Ruta /calibration.
const CalibrationScreenPL   = lazy(cargaDeTrozo(() => import('./components/onboarding/pl/CalibrationScreenPL')))
// Legacy 5-act onboarding REMOVED (Operación Feten 1.0.C)

// Sprint PrimeLab Fase 5: calendario 3 modos. Ruta /calendario.
// Sprint PrimeLab Fase 8: Aria chat refactor. Ruta /aria.
const AriaChatPL = lazy(cargaDeTrozo(() => import('./components/ai/pl/AriaChatPL')))
// Sprint PrimeLab Fase 9: reportar lesión. Ruta /reportar-lesion.
const PLInjuryReportFlow = lazy(cargaDeTrozo(() => import('./components/injury/pl/PLInjuryReportFlow')))
// Sprint PrimeLab Fase 6: nutrición seed mínimo. Ruta /nutricion.
const NutricionPagePL = lazy(cargaDeTrozo(() => import('./components/nutricion/pl/NutricionPagePL')))
// Sprint PrimeLab v1.1.5: stub progreso + perfil. Rutas /progreso /perfil.
const ProgresoPagePL = lazy(cargaDeTrozo(() => import('./components/progreso/ProgresoPagePL')))
// Sprint 15 Ola 1.7.2: Vista Perfil Rica (replaces PerfilPagePL)
const PerfilPL = lazy(cargaDeTrozo(() => import('./components/perfil/PerfilPL')))
// v1.3.1: founder-only analytics dashboard. Ruta /internal/dashboard.
const DashboardInterno = lazy(cargaDeTrozo(() => import('./components/internal/DashboardInterno')))

function ProtectedRoute({ children }) {
  const { user, profile, loading, profileResolved } = useAuthStore()
  const location = useLocation()
  if (loading) return <FullScreenLoader />
  if (!user) return <Navigate to="/auth" replace />
  // QA-E2E bug#3: NO decidir onboarding-vs-app hasta resolver el perfil del usuario
  // ACTUAL. El hint scopeado por uid resuelve al instante a un usuario conocido (sin
  // flash); el loader solo se ve cuando de verdad no se sabe (signup fresco / race).
  if (!profileResolved) return <FullScreenLoader />
  // Bloque C.8 + cinceladura OPCIÓN A 2026-05-28 · onboarding canonical es /onboarding/v2.
  // La pantalla "El Ascenso" (/transicion/*) ejecuta el pipeline post-onboarding y NO
  // debe rebotar mientras corre.
  // bug#3: perfil resuelto a null (cuenta nueva sin fila aún) o incompleto → onboarding ·
  // NUNCA caer a la app con un perfil ajeno o ausente.
  if (
    (!profile || !profile.onboarding_completed)
    && !location.pathname.startsWith('/onboarding')
    && !location.pathname.startsWith('/transicion')
  ) {
    return <Navigate to="/onboarding/v2" replace />
  }
  return children
}

function LandingOrToday() {
  const { user, profile, loading, profileResolved } = useAuthStore()
  if (loading) return <FullScreenLoader />
  // bug#3: con sesión, esperar a resolver el perfil del usuario actual antes de decidir
  if (user && !profileResolved) return <FullScreenLoader />
  // Estado 4 · sesión activa + onboarding completo · directo a Today (PWA experiencia real)
  if (user && profile?.onboarding_completed) return <Navigate to="/today" replace />
  // Estado 2 · sesión activa, onboarding incompleto (o perfil nuevo aún sin fila) · onboarding V2
  if (user && !profile?.onboarding_completed) return <Navigate to="/onboarding/v2" replace />
  // Estados 1 + 3 · sin sesión (nuevo o expirado) · LANDING SIEMPRE
  // Doctrina #13 primera impresión · doctrina #6 cero fricción.
  // NOTA: flag pl_has_seen_landing pre-existente NO se usa para skip landing porque rompía PWA UX (PR Operación Feten v1.0.2).
  return <LandingPage />
}

function OnboardingRoute({ children }) {
  const { user, profile, loading, profileResolved } = useAuthStore()
  if (loading) return <FullScreenLoader />
  if (!user) return <Navigate to="/auth" replace />
  // bug#3: esperar a resolver antes de rebotar (evita decidir con perfil ajeno/ausente)
  if (!profileResolved) return <FullScreenLoader />
  if (profile?.onboarding_completed) return <Navigate to="/" replace />
  return children
}

function FullScreenLoader() {
  // Parte 3 · ya NO renderiza el splash con logo. Carga quieta entre pasos.
  return <RouteTransitionFallback />
}

export default function App() {
  const { setUser, setLoading, clearSession, fetchProfile, hydrateProfileHint, user } = useAuthStore()

  // Hash handler — Supabase redirige con hash params tras email auth.
  // Éxito: #access_token=... → SDK lo procesa via detectSessionInUrl: true.
  // Error: #error=... → redirigir a /auth/expired (otp_expired) o /auth.
  useEffect(() => {
    const hash = window.location.hash
    if (!hash) return
    const params = new URLSearchParams(hash.substring(1))
    const error = params.get('error')
    const errorCode = params.get('error_code')
    if (error) {
      // Limpiar el hash de la URL antes de redirigir
      window.history.replaceState(null, '', window.location.pathname)
      if (errorCode === 'otp_expired') {
        window.location.replace('/auth/expired')
      } else {
        window.location.replace('/auth')
      }
    }
    // access_token: SDK lo procesa automáticamente, onAuthStateChange SIGNED_IN
    // se encarga del upsert + redirect a /hoy.
  }, []) // once on mount

  // Auth listener — runs once on mount, cleans up subscription on unmount
  useEffect(() => {
    // Safety timeout: if getSession() hangs (e.g. navigator.locks contention in dev
    // with HMR creating multiple Supabase client instances), unblock loading after 3s.
    const loadingTimeout = setTimeout(() => setLoading(false), 3000)

    supabase.auth.getSession()
      .then(({ data: { session } }) => {
        clearTimeout(loadingTimeout)
        setUser(session?.user ?? null)
        setLoading(false)
      })
      .catch(() => {
        // Lock contention or network error — unblock UI, user will see auth screen
        clearTimeout(loadingTimeout)
        setLoading(false)
      })

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        setUser(session?.user ?? null)
        // Post-email-confirmation: si hay draft en localStorage, upsert a user_profiles,
        // luego navegar a /hoy. Solo en SIGNED_IN (no TOKEN_REFRESHED).
        if (event === 'SIGNED_IN' && session?.user?.id) {
          try {
            // S10-ALPHA-01 fix: read PL_ONBOARDING_PROFILE first (written by OnboardingCanonicalPL / OnboardingFlowPL),
            // fall back to PL_ONBOARDING_DRAFT (written by legacy OnboardingFlow).
            const draft = localStorage.getItem('PL_ONBOARDING_PROFILE') || localStorage.getItem('PL_ONBOARDING_DRAFT')
            if (draft) {
              const profileData = JSON.parse(draft)
              // Upsert biometric data to canonical table
              await supabase.from('user_profiles').upsert({ id: session.user.id, ...profileData, onboarding_completed: true })
              // Set onboarding_completed in profiles so ProtectedRoute redirect resolves
              await supabase.from('profiles').upsert({ id: session.user.id, onboarding_completed: true })
              // Remove both draft keys before redirecting — setItem here was a bug
              // that re-saved the draft on every SIGNED_IN, creating a calibration loop.
              localStorage.removeItem('PL_ONBOARDING_PROFILE')
              localStorage.removeItem('PL_ONBOARDING_DRAFT')
              // QA-E2E bug#2: borrar también el draft v2 REAL (clave minúscula scopeada),
              // no solo la mayúscula legacy — onboarding completado, draft consumido.
              clearAllOnboardingV2Drafts()
              // Sprint 14b Phase 3: redirect to /calibration (ritual screen) instead of /today
              // so the motor can generate the initial plan during the 10s animation.
              window.location.replace('/calibration')
            }
          } catch { /* no bloqueamos el flujo */ }
        }
      } else if (event === 'SIGNED_OUT') {
        clearSession()
      }
    })

    return () => subscription.unsubscribe()
  }, []) // runs once on mount

  // Profile fetch — stable primitive dep, fires only when user ID actually changes
  useEffect(() => {
    if (user?.id) {
      // QA-E2E bug#3: resetear el perfil del usuario anterior ANTES de fetch (cero
      // ventana con perfil ajeno) · hint scopeado por uid resuelve al instante a un
      // usuario conocido (sin flash) · fetchProfile trae la verdad del backend.
      useAuthStore.setState({ profile: null, profileResolved: false })
      hydrateProfileHint(user.id)
      fetchProfile(user.id)
      // A2 · identify SOLO si PostHog llegó a inicializarse (key + consentimiento analytics)
      // LAZY: el singleton lo publica index.js en window.__posthog tras el import dinámico;
      // sin consentimiento no existe → no-op (idéntico al guard previo con .__loaded).
      const _ph = window.__posthog
      if (_ph?.__loaded) _ph.identify(user.id, { email: user.email })
      // C.2 · retención D1/D7/D30 sin PostHog: session_start en analytics_events
      // (SQL lo reconstruye · fire-and-forget, nunca rompe)
      import('./lib/analytics').then(m => m.trackEvent('session_start', {})).catch(() => {})
    } else {
      useAuthStore.setState({ profile: null, profileResolved: false })
    }
  }, [user?.id])

  // Service worker
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      registerServiceWorker()
    } else {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then(regs => {
          regs.forEach(r => r.unregister())
        })
      }
    }
  }, [])

  return (
    <Sentry.ErrorBoundary fallback={({ error, resetError }) => {
      /* COLA:la-pestana-de-ayer-se-rompe-tras-la-promocion · si lo que falló es un TROZO (la pestaña de ayer pidiendo un hash que ya no existe tras una
         promocion), «Reintentar» no sirve: vuelve a montar el mismo import roto, que es exactamente lo que REALIDAD vio en el dispositivo. Se recarga
         una vez, con marca y ventana, y mientras se dice lo que esta pasando. Cualquier otro error se queda con su pantalla y su boton. */
      /* COLA:alta-termina-en-pantalla-de-error · lo que se PINTA se decide por el error, no por «¿he recargado?»: este fallback se vuelve a llamar en
         cada render del estado de error, y `recargaUnaVez` devuelve false a partir de la segunda vez por su propia ventana — así que encadenarlas
         devolvía la pantalla sin salida en el segundo render. Una sola forma para las dos boundaries. */
      if (esErrorDeTrozo(error)) {
        recargaUnaVez('el boundary vio un error de trozo')
        return (
          <div data-testid="boundary-recargando" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'var(--pl-cream)', flexDirection: 'column', gap: 12, padding: 24, textAlign: 'center' }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: 'var(--pl-ink)' }}>Hay una version nueva</h2>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--pl-ink-soft)' }}>La estoy cargando. No hace falta que hagas nada.</p>
          </div>
        )
      }
      return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--pl-cream)', flexDirection: 'column', gap: 16, padding: 24, textAlign: 'center' }}>
        <p style={{ fontSize: 32, margin: 0 }}>⚠️</p>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: 'var(--pl-ink)' }}>Algo salió mal</h2>
        <p style={{ margin: 0, fontSize: 14, color: 'var(--pl-ink-soft)' }}>El error ha sido reportado. Recarga para continuar.</p>
        <button onClick={resetError} style={{ marginTop: 8, padding: '12px 24px', borderRadius: 12,
          border: 'none', background: 'var(--pl-terracota)', color: 'var(--pl-cream)',
          fontFamily: 'var(--font-sans)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Reintentar</button>
      </div>
      )
    }}>
    <BrowserRouter>
      <AuriaContextProvider>
      <ToastProvider>
        <div className="noise">
          <SwUpdateBanner />
          <AddToHomeScreenPrompt />
          {/* NOCHE 2 · A2 · el banner de cookies existía (granular, digno) y NUNCA se
              montaba (0 refs, F7) — mientras Sentry/analytics corrían pre-consentimiento.
              Se autogestiona: solo aparece sin consentimiento válido almacenado. */}
          <CookieConsentBanner
            onAccepted={(c) => { if (c?.analytics) window.location.reload() }}
          />
          <Suspense fallback={<FullScreenLoader />}>
            <AuriaSheet />
            {/* LA PIEL F2 · aurIA se abre desde UN SOLO punto consistente (FabAuria por pantalla).
               El botón-chat flotante "Esto no me cuadra" (FeedbackButton) dejaba DOS flotantes con
               la barra → retirado del montaje global. Su función (reportar discrepancia) se pliega
               en la puerta AVISAR de aurIA en F8; el componente se conserva para reusar su lógica. */}
            <Routes>
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/auth/expired" element={<AuthExpiredPage />} />
              <Route path="/auth/reset" element={<AuthPage />} />
              {/* 1.0.C/BUG-01: /onboarding redirects to canonical 17-step flow */}
              <Route path="/onboarding" element={<Navigate to="/onboarding/v2" replace />} />
              {/* Sprint 8.5 F0.3 — ruta pública para política de privacidad */}
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              {/* Sprint PrimeLab Fase 1: playground del design system */}
              <Route path="/playground" element={<PLPlaygroundPage />} />
              {/* P2 · 2026-05-28 · Design System V2 showcase · ruta pública para validación visual */}
              <Route path="/design-system" element={<DesignSystemPage />} />
              {/* Bloque C.1 · QA visual público · no auth required */}
              <Route path="/playground/hub-c1" element={<HubPlaygroundC1 />} />
              <Route path="/hub-preview" element={<HubPreviewPage />} />
              <Route path="/hub-charts" element={<HubCanonicalShowcase />} />
              <Route path="/goal-preview" element={<GoalPhaseMapPreview />} />
              <Route path="/today-preview" element={<TodayPreviewPage />} />
              <Route path="/score-preview" element={<PrimeScorePreview />} />
              <Route path="/reasoning-preview" element={<ReasoningPreview />} />
              {/* ENTORNO QA RENDERIZADO · /dev/qa-preview gated por REACT_APP_QA_BYPASS + hostname
                  (NUNCA en la prod pública · ver lib/qaBypass.js). Fuera del NODE_ENV-block para
                  sobrevivir a un build production-mode servido con history-fallback (serve -s). */}
              {_QA_BYPASS && (
                <Route path="/dev/qa-preview" element={<QAPreviewPage />} />
              )}
              {/* DEV EFÍMERO · surfacea el token de la sesión propia (loop de validación · flag OFF al acabar) */}
              {_DEV_TOKEN_ROUTE && (
                <Route path="/dev/session-token" element={<SessionTokenDev />} />
              )}
              {/* F3-LV · escaparates dev · HIGIENE: fuera del build de producción (no exponer componentes internos) */}
              {process.env.NODE_ENV !== 'production' && (
                <>
                  <Route path="/dev/linea-vida" element={<LifePathShowcase />} />
                  <Route path="/dev/components" element={<ComponentsShowcase />} />
                  <Route path="/dev/sport" element={<SportAccordionDemo />} />
                  <Route path="/dev/progress" element={<ProgressCardsDemo />} />
                  <Route path="/dev/calendario" element={<CalendarDemo />} />
                  <Route path="/dev/linea-vida-v2" element={<LifeBarDemo />} />
                  <Route path="/dev/today-card" element={<TodayCardShowcase />} />
                  <Route path="/dev/fueling-sesion" element={<FuelingSesionShowcase />} />
                  <Route path="/dev/por-que-estos-numeros" element={<PorQueEstosNumerosShowcase />} />
                  <Route path="/dev/today-respira" element={<TodayRespiraShowcase />} />
                  <Route path="/dev/onboarding-preview" element={<OnboardingPreviewShowcase />} />
                  {/* HITO 2 · A4 generación — cata del ritual de calibración (imperio streaming) a 390px */}
                  <Route path="/dev/calibration" element={<CalibrationScreenPL onComplete={() => {}} />} />
                </>
              )}
              {/* Sprint 14b Phase 3: calibration ritual screen (ProtectedRoute — fires after SIGNED_IN) */}
              <Route path="/calibration" element={<ProtectedRoute><CalibrationScreenPL onComplete={() => window.location.replace('/today')} /></ProtectedRoute>} />
              {/* 1.0.C: Legacy 5-act onboarding removed — redirect to canonical */}
              {/* LA PIEL F2 · DOMINIOS murió como destino · su contenido se fusiona en Progreso (F7).
                 /domains + /dominios redirigen a /progress?tab=partes. DominiosV2Page se conserva
                 (F7 injerta su contenido) pero ya no es ruta viva. */}
              <Route path="/domains" element={<Navigate to="/progress?tab=partes" replace />} />
              <Route path="/domains/:id" element={<Navigate to="/progress?tab=partes" replace />} />
              <Route path="/dominios" element={<Navigate to="/progress?tab=partes" replace />} />
              <Route path="/dominios/:id" element={<Navigate to="/progress?tab=partes" replace />} />
              {/* P5 · rollback path */}
              <Route path="/domains/legacy" element={<ProtectedRoute><AppLayout><DomainsPagePL /></AppLayout></ProtectedRoute>} />
              <Route path="/domains/legacy/:id" element={<ProtectedRoute><AppLayout><DomainDeepDive /></AppLayout></ProtectedRoute>} />
              {/* Bloque C.1 (Feten Fase 2) — Wearables Hub refundido coach absoluto */}
              {/* P6 · Hub V2 · día-1 + datos · 3 niveles zoom · sin AppLayout · monta BottomNav+FabAuria propios */}
              <Route path="/hub" element={<ProtectedRoute><HubV2Page /></ProtectedRoute>} />
              {/* P6 · rollback path · sigue accesible */}
              <Route path="/hub/legacy" element={<ProtectedRoute><AppLayout><WearablesHubPage /></AppLayout></ProtectedRoute>} />
              {/* Sprint 19 legacy fallback bajo /hub-legacy hasta deprecation */}
              <Route path="/hub-legacy" element={<ProtectedRoute><AppLayout><WearablesHubPL /></AppLayout></ProtectedRoute>} />
              {/* Sprint 14b Phase 2: canonical surfaces (ProtectedRoute + AppLayout) */}
              {/* P5 · Calendar V2 · 3 vistas DS + dos pilares + chaos labels + P7 stubs */}
              {/* Sin AppLayout · CalendarV2Page ya monta BottomNav + FabAuria del DS */}
              <Route path="/calendar" element={<ProtectedRoute><CalendarV2Page /></ProtectedRoute>} />
              {/* Bloque C.4 (Feten Fase 2) — Dominios refundido 8 + jerarquía dinámica */}
              <Route path="/dominios-c4" element={<ProtectedRoute><AppLayout><DomainsC4Page /></AppLayout></ProtectedRoute>} />
              {/* Bloque C.5 (Feten Fase 2) — Progreso refundido 4 niveles */}
              <Route path="/progreso-c5" element={<ProtectedRoute><AppLayout><ProgressC5Page /></AppLayout></ProtectedRoute>} />
              {/* Bloque C.2 (Feten Fase 2) — Integrations Strava OAuth + wearables */}
              <Route path="/integrations" element={<ProtectedRoute><AppLayout><IntegrationsPage /></AppLayout></ProtectedRoute>} />
              {/* P3 V2 (2026-05-28) · onboarding sinfónico V2 · canon ÚNICO · doc maestro. */}
              {/* SALTO:onboarding-10 · T2 · decisión del founder (2026-09-14): «invitado hasta "Tu plan"». El alta sale de `ProtectedRoute`: se
                  puede contestar entera sin cuenta, y la cuenta se crea al final. `AltaAbierta` conserva lo único que `ProtectedRoute` aportaba aquí
                  —que quien ya terminó su alta no vuelva a entrar— sin exigir sesión para empezarla. */}
              <Route path="/onboarding/v2" element={<AltaAbierta><PLErrorBoundary scope="onboarding-v2"><OnboardingV2Page /></PLErrorBoundary></AltaAbierta>} />
              {/* Política coexistencia P3 · /onboarding/sinfonico V1 → REDIRECT a V2. El
                  componente OnboardingSinfonicoPage queda en el árbol como referencia
                  histórica · ningún usuario aterriza ahí. Si en V2.x necesitamos rollback
                  rápido a V1, basta con apuntar este Route a <OnboardingSinfonicoPage>. */}
              <Route path="/onboarding/sinfonico" element={<Navigate to="/onboarding/v2" replace />} />
              {/* Cinceladura OPCIÓN A 2026-05-28 · pantalla transición "El Ascenso" ·
                  4 fases (vacío → arco emerge → bolita ascends → orquestación pipeline) ·
                  aparece UNA vez entre Resumen y Today.
                  cables-v3 · PLErrorBoundary blinda · si Ascenso peta, fallback cream. */}
              <Route path="/transicion/ascenso" element={<ProtectedRoute><PLErrorBoundary scope="ascenso"><AscensoScreenPL /></PLErrorBoundary></ProtectedRoute>} />
              <Route path="/transicion/ficha" element={<ProtectedRoute><PLErrorBoundary scope="ficha-del-alta"><FichaDelAltaScreen /></PLErrorBoundary></ProtectedRoute>} />
              {/* SALTO:ficha-del-10 · D6 · la revisión semanal (tesis 4): lo hecho sin reproche, qué cambió y por qué con su fila, y qué cambia la
                  semana que viene. Dentro de `AppLayout`: es una superficie de la app, no un paso del alta. */}
              <Route path="/revision" element={<ProtectedRoute><AppLayout><PLErrorBoundary scope="prime-review"><PrimeReviewScreen /></PLErrorBoundary></AppLayout></ProtectedRoute>} />
              {/* SALTO:ficha-del-10 · D8 · la voz de descarte (pieza 12): lo que NO se te va a prescribir, y por qué, con la entrada de la KB detrás. */}
              <Route path="/ficha/lo-que-no-entra" element={<ProtectedRoute><AppLayout><PLErrorBoundary scope="lo-que-no-entra"><LoQueNoEntraScreen /></PLErrorBoundary></AppLayout></ProtectedRoute>} />
              {/* Sprint 15 Ola 4 legacy fallback */}
              {/* A4 · M6 · UNA SOLA SUPERFICIE DE CALENDARIO.
                  `/calendario` servía CalendarPL (v1) mientras `/calendar` sirve CalendarV2Page.
                  Dos calendarios vivos: el v1 leia el dialecto muerto de las comidas y
                  no tiene el DayView nuevo, así que el mismo usuario veía un día distinto
                  según por qué URL entrase. Se redirige al canónico, como ya se hace con
                  /progreso → /progress. COLA:rutas-legacy-app · el v1 y sus rutas legacy, al cementerio (C-14). */}
              <Route path="/calendario" element={<Navigate to="/calendar" replace />} />
              <Route path="/aria"       element={<ProtectedRoute><AppLayout><AriaPL /></AppLayout></ProtectedRoute>} />
              {/* Legacy fallbacks preserved */}
              <Route path="/aria/legacy"       element={<AriaChatPL />} />
              {/* Sprint PrimeLab Fase 9: reportar lesión */}
              <Route path="/reportar-lesion" element={<PLInjuryReportFlow />} />
              {/* Sprint PrimeLab Fase 6: nutrición seed */}
              <Route path="/nutricion" element={<ProtectedRoute><NutricionPagePL /></ProtectedRoute>} />
              {/* Sprint 14b Phase 2: canonical progress surface */}
              {/* P6 · /progress canonical EN (HYGIENE-11) · /progreso → 301 redirect */}
              <Route path="/progress" element={<ProtectedRoute><ProgressV2Page /></ProtectedRoute>} />
              <Route path="/progreso" element={<Navigate to="/progress" replace />} />
              {/* P6 · rollback paths */}
              <Route path="/progress/legacy" element={<ProtectedRoute><AppLayout><ProgressPagePL /></AppLayout></ProtectedRoute>} />
              <Route path="/progreso/legacy" element={<ProgresoPagePL />} />
              {/* A4 · M5 · /perfil bajo ProtectedRoute + AppLayout como el resto de rutas de
                  sesión. Era la ÚNICA pantalla de usuario que se montaba sin guardia: entrar
                  sin sesión renderizaba el perfil vacío en vez de mandar a login. */}
              <Route path="/perfil"   element={<ProtectedRoute><AppLayout><PerfilPL /></AppLayout></ProtectedRoute>} />
              {/* v1.3.1: founder-only analytics dashboard (redirects to /hoy if not founder) */}
              <Route path="/internal/dashboard" element={<DashboardInterno />} />

              {/* Sprint 14b Phase 2: canonical session surface (full-screen, no tab bar) */}
              <Route path="/session" element={<ProtectedRoute><SessionPL /></ProtectedRoute>} />

              {/* Sprint 5 rutas con AppLayout (tab bar) */}
              {/* P4 V2 (2026-05-28) · Today V2 canonical · 3 tercios + bolita-testigo +
                  TimelineLifePath + primer minuto + día 2 · doc maestro sec 2. Si V2
                  necesita rollback rápido, apuntar este Route a
                  <AppLayout><TodayPL /></AppLayout>. */}
              <Route path="/today" element={<ProtectedRoute><PLErrorBoundary scope="today"><TodayV2Page /></PLErrorBoundary></ProtectedRoute>} />
              <Route path="/today/legacy" element={<ProtectedRoute><AppLayout><PLErrorBoundary scope="today-legacy"><TodayPL /></PLErrorBoundary></AppLayout></ProtectedRoute>} />
              {/* Sprint 15 Ola 1 Item 2 — legacy /dashboard redirects to canonical /today */}
              <Route path="/dashboard" element={<Navigate to="/today" replace />} />
              {/* Sprint 8: aurIA sale del tab bar → AuriaCommandOverlay. /coach se conserva como ruta directa para deep-links. */}
              <Route path="/coach" element={<ProtectedRoute><AppLayout><AICoachPage /></AppLayout></ProtectedRoute>} />
              {/* Sprint 15 Ola 1 Item 2 — legacy /nutrition redirects to canonical /nutricion */}
              <Route path="/nutrition" element={<Navigate to="/nutricion" replace />} />
              <Route path="/mobility" element={<ProtectedRoute><AppLayout><MobilityPage /></AppLayout></ProtectedRoute>} />
              {/* P6 · /progress es ahora canonical · este redirect retro queda obsoleto */}
              {/* Sprint 15 Ola 1 Item 2 — legacy /training redirects to canonical /today */}
              <Route path="/training" element={<Navigate to="/today" replace />} />
              <Route path="/health" element={<ProtectedRoute><AppLayout><HealthSyncPage /></AppLayout></ProtectedRoute>} />
              <Route path="/wearables" element={<ProtectedRoute><AppLayout><WearablesSettings /></AppLayout></ProtectedRoute>} />

              {/* Sprint 6: landing si no auth, redirect a /today si auth */}
              <Route path="/" element={<LandingOrToday />} />
              <Route path="/habits" element={<Navigate to="/today" replace />} />
              <Route path="/wellness" element={<Navigate to="/coach" replace />} />

              <Route path="*" element={<Navigate to="/today" replace />} />
            </Routes>
          </Suspense>
        </div>
      </ToastProvider>
      </AuriaContextProvider>
      <Toaster
        position="top-right"
        toastOptions={{
          style: { background: 'var(--pl-surface-2)', color: 'var(--pl-text)', border: '0.5px solid var(--pl-border-2)', borderRadius: 'var(--r-md)', fontFamily: 'var(--pl-font-sans)' },
          success: { iconTheme: { primary: 'var(--pl-terracota)', secondary: 'var(--pl-cream)' } },
          error: { iconTheme: { primary: 'var(--pl-error)', secondary: 'var(--pl-cream)' } },
        }}
      />
    </BrowserRouter>
    </Sentry.ErrorBoundary>
  )
}
